'use client';
import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { currentUser, achievements, levels } from './mockData';
import type { User, Achievement, Level } from './mockData';

export interface Notification {
  id: string;
  type: 'achievement' | 'streak' | 'lesson' | 'system';
  title: string;
  message: string;
  time: string;
  read: boolean;
  icon: string;
}

interface AppContextType {
  isAuthenticated: boolean;
  user: User;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  notifications: Notification[];
  unreadCount: number;
  markAllRead: () => void;
  markRead: (id: string) => void;
  userLevels: Level[];
  completeLesson: (lessonId: string, xpEarned: number, accuracy: number) => void;
  completeLevelTest: (levelNumber: number, score: number, passed: boolean) => void;
  userAchievements: Achievement[];
  totalXp: number;
  weeklyXp: number;
  streak: number;
  completedLevelTests: Record<number, { score: number; passed: boolean }>;
}

const DEMO_CREDENTIALS = { email: 'sophie.moreau@vikacomptable.fr', password: 'Vika2026!' };
const ADMIN_CREDENTIALS = { email: 'admin@vikacomptable.fr', password: 'AdminVika2026!' };
const STORAGE_KEY = 'vika_comptable_progress';

const defaultNotifications: Notification[] = [
  { id: 'notif-001', type: 'achievement', title: 'Succès débloqué!', message: 'Vous avez complété 10 leçons — Badge "10 Leçons" obtenu!', time: 'Il y a 2h', read: false, icon: '🏆' },
  { id: 'notif-002', type: 'streak', title: 'Série de 12 jours!', message: 'Continuez comme ça! Vous êtes sur une série de 12 jours consécutifs.', time: 'Il y a 5h', read: false, icon: '🔥' },
  { id: 'notif-003', type: 'lesson', title: 'Nouvelle leçon disponible', message: 'La leçon "La règle du débit-crédit" est maintenant disponible.', time: 'Hier', read: true, icon: '📚' },
  { id: 'notif-004', type: 'system', title: 'Bienvenue sur Vika Comptable!', message: 'Commencez votre parcours d\'apprentissage comptable dès aujourd\'hui.', time: 'Il y a 3 jours', read: true, icon: '👋' },
];

interface SavedProgress {
  userLevels: Level[];
  totalXp: number;
  weeklyXp: number;
  lessonsCompleted: number;
  completedLevelTests: Record<number, { score: number; passed: boolean }>;
  userAchievements: Achievement[];
  notifications: Notification[];
}

function loadProgress(): SavedProgress | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SavedProgress;
  } catch {
    return null;
  }
}

function saveProgress(data: SavedProgress) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [user, setUser] = useState<User>(currentUser);
  const [notifications, setNotifications] = useState<Notification[]>(defaultNotifications);
  const [userLevels, setUserLevels] = useState<Level[]>(levels);
  const [userAchievements, setUserAchievements] = useState<Achievement[]>(achievements);
  const [totalXp, setTotalXp] = useState(currentUser.xp);
  const [weeklyXp, setWeeklyXp] = useState(currentUser.weeklyXp);
  const [streak] = useState(currentUser.streak);
  const [completedLevelTests, setCompletedLevelTests] = useState<Record<number, { score: number; passed: boolean }>>({});
  const [hydrated, setHydrated] = useState(false);

  // Load persisted progress on mount
  useEffect(() => {
    const saved = loadProgress();
    if (saved) {
      setUserLevels(saved.userLevels);
      setTotalXp(saved.totalXp);
      setWeeklyXp(saved.weeklyXp);
      setUser((prev) => ({ ...prev, xp: saved.totalXp, weeklyXp: saved.weeklyXp, lessonsCompleted: saved.lessonsCompleted }));
      setCompletedLevelTests(saved.completedLevelTests ?? {});
      setUserAchievements(saved.userAchievements ?? achievements);
      if (saved.notifications?.length) setNotifications(saved.notifications);
    }
    setHydrated(true);
  }, []);

  // Persist whenever key state changes
  useEffect(() => {
    if (!hydrated) return;
    saveProgress({
      userLevels,
      totalXp,
      weeklyXp,
      lessonsCompleted: user.lessonsCompleted,
      completedLevelTests,
      userAchievements,
      notifications,
    });
  }, [hydrated, userLevels, totalXp, weeklyXp, user.lessonsCompleted, completedLevelTests, userAchievements, notifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    await new Promise((r) => setTimeout(r, 1200));
    const isValid =
      (email === DEMO_CREDENTIALS.email && password === DEMO_CREDENTIALS.password) ||
      (email === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password);
    if (isValid) {
      const isAdmin = email === ADMIN_CREDENTIALS.email;
      setUser((prev) => ({ ...prev, isAdmin, email }));
      setIsAuthenticated(true);
    }
    return isValid;
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const markRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const completeLesson = useCallback((lessonId: string, xpEarned: number, accuracy: number) => {
    setUserLevels((prev) =>
      prev.map((level) => {
        const lessonIndex = level.lessons.findIndex((l) => l.id === lessonId);
        if (lessonIndex === -1) return level;

        const updatedLessons = level.lessons.map((lesson, idx) => {
          if (lesson.id === lessonId) {
            return { ...lesson, status: 'completed' as const, accuracy, completedAt: new Date().toISOString() };
          }
          if (idx === lessonIndex + 1 && lesson.status === 'locked') {
            return { ...lesson, status: 'available' as const };
          }
          return lesson;
        });

        const completedCount = updatedLessons.filter((l) => l.status === 'completed').length;
        const completionPercent = Math.round((completedCount / updatedLessons.length) * 100);
        return { ...level, lessons: updatedLessons, completionPercent };
      })
    );

    setTotalXp((prev) => prev + xpEarned);
    setWeeklyXp((prev) => prev + xpEarned);
    setUser((prev) => ({
      ...prev,
      xp: prev.xp + xpEarned,
      weeklyXp: prev.weeklyXp + xpEarned,
      lessonsCompleted: prev.lessonsCompleted + 1,
    }));

    const newNotif: Notification = {
      id: `notif-${Date.now()}`,
      type: 'lesson',
      title: 'Leçon terminée!',
      message: `Vous avez gagné ${xpEarned} XP avec ${accuracy}% de précision.`,
      time: "À l'instant",
      read: false,
      icon: '⭐',
    };
    setNotifications((prev) => [newNotif, ...prev]);

    // Check achievements
    setUserAchievements((prev) =>
      prev.map((ach) => {
        if (ach.isUnlocked) return ach;
        if (ach.key === 'first_lesson') return { ...ach, isUnlocked: true, unlockedAt: new Date().toISOString() };
        return ach;
      })
    );
  }, []);

  const completeLevelTest = useCallback((levelNumber: number, score: number, passed: boolean) => {
    setCompletedLevelTests((prev) => ({ ...prev, [levelNumber]: { score, passed } }));

    if (passed) {
      const bonusXp = levelNumber * 100;
      setTotalXp((prev) => prev + bonusXp);
      setWeeklyXp((prev) => prev + bonusXp);
      setUser((prev) => ({ ...prev, xp: prev.xp + bonusXp, weeklyXp: prev.weeklyXp + bonusXp }));

      // Unlock next level
      setUserLevels((prev) =>
        prev.map((level) => {
          if (level.number === levelNumber + 1 && !level.isUnlocked) {
            // Unlock first lesson of next level
            const updatedLessons = level.lessons.map((ls, idx) =>
              idx === 0 ? { ...ls, status: 'available' as const } : ls
            );
            return { ...level, isUnlocked: true, lessons: updatedLessons };
          }
          return level;
        })
      );

      const newNotif: Notification = {
        id: `notif-lvl-${Date.now()}`,
        type: 'achievement',
        title: `Niveau ${levelNumber} complété! 🎉`,
        message: `Score: ${score}%. +${levelNumber * 100} XP bonus! Niveau ${levelNumber + 1} débloqué!`,
        time: "À l'instant",
        read: false,
        icon: '🏆',
      };
      setNotifications((prev) => [newNotif, ...prev]);

      // Check level-specific achievements
      setUserAchievements((prev) =>
        prev.map((ach) => {
          if (ach.isUnlocked) return ach;
          if (ach.key === 'vat_master' && levelNumber === 6) return { ...ach, isUnlocked: true, unlockedAt: new Date().toISOString() };
          if (ach.key === 'accounting_champion' && levelNumber === 10) return { ...ach, isUnlocked: true, unlockedAt: new Date().toISOString() };
          return ach;
        })
      );
    }
  }, []);

  return (
    <AppContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        notifications,
        unreadCount,
        markAllRead,
        markRead,
        userLevels,
        completeLesson,
        completeLevelTest,
        userAchievements,
        totalXp,
        weeklyXp,
        streak,
        completedLevelTests,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
